import type { VoiceCall } from "./types";

// Phase 4 §5c — disposition tagging for the /voice/calls reporting view
// and (future) the Phase 2.5 Reports surface. Kept intentionally pure +
// dependency-free so the two views compute the same tags from the same
// inputs without any drift.
//
// Mapping is derived, never stored. If we want to re-tag historical rows,
// flipping the helper is a pure-code change — no migration required.
//
// | UI tag       | Source                                                    |
// |--------------|-----------------------------------------------------------|
// | Answered     | outcome = 'forwarded' (a human picked up)                 |
// | AI Answered  | outcome = 'answered-ai' (AI handled the full call)        |
// | Left a VM    | outcome in ('voicemail','voicemail-heard')                |
// | Missed       | outcome = 'missed'                                        |
// | In Progress  | outcome = 'in-progress' AND created_at > now - 10 min     |
// | Sent a Text  | outcome in (…) AND there's an outbound SMS from the same  |
// |              | staff number within ±60 min of this call's created_at     |
// | —            | anything else (shouldn't happen post-5c; catches orphan   |
// |              | in-progress rows the janitor hasn't swept yet)            |
//
// The "Sent a Text" tag is COMPOSITE — it can stack with Answered / Missed /
// Left a VM / In Progress. Everything else is mutually exclusive. We return
// a primary tag for the table cell and a `sentText` flag for a secondary
// badge so both signals are visible in one row.

export type CallTag =
  | "answered"
  | "ai_answered"
  | "left_vm"
  | "missed"
  | "in_progress"
  | "unknown";

export const CALL_TAG_LABELS: Record<CallTag, string> = {
  answered: "Answered",
  ai_answered: "AI Answered",
  left_vm: "Left a VM",
  missed: "Missed",
  in_progress: "In Progress",
  unknown: "—",
};

// Tailwind class string for the primary-tag badge. Kept here so the view can
// import labels + colors from one place and we don't risk divergence when
// the Reports view lands.
export const CALL_TAG_STYLES: Record<CallTag, string> = {
  answered: "bg-green-700 text-white",
  ai_answered: "bg-emerald-600 text-white",
  left_vm: "bg-red-600 text-white",
  missed: "bg-gray-600 text-gray-300",
  in_progress: "bg-yellow-600 text-white",
  unknown: "bg-white/10 text-tribal-muted",
};

export type TaggedCall = {
  call: VoiceCall;
  tag: CallTag;
  sentText: boolean;
};

// Shape the "did staff SMS this caller within ±60 min" index uses. Built
// client-side by the caller from an outbound-SMS fetch (the /sms/messages
// endpoint), and passed in. Today this is always `[]` because the SMS
// reporting surface lands with Phase 2.5; the API shape is in place so
// wiring the real index in is a one-liner later.
//
// Each entry is one outbound SMS: `to_number` is the caller (who we texted),
// `created_at_ms` is epoch millis for cheap ±60 min math.
export type SmsIndexEntry = {
  to_number: string;
  created_at_ms: number;
};

const IN_PROGRESS_FRESH_MS = 10 * 60 * 1000;
const SENT_TEXT_WINDOW_MS = 60 * 60 * 1000;

function normalizeDigits(phone: string | null | undefined): string {
  if (!phone) return "";
  return phone.replace(/\D+/g, "");
}

function hasSentTextFor(
  call: VoiceCall,
  callTimeMs: number,
  smsIndex: readonly SmsIndexEntry[]
): boolean {
  if (smsIndex.length === 0) return false;
  const callerDigits = normalizeDigits(call.caller_number);
  if (!callerDigits) return false;
  for (const entry of smsIndex) {
    if (Math.abs(entry.created_at_ms - callTimeMs) > SENT_TEXT_WINDOW_MS) continue;
    if (normalizeDigits(entry.to_number) === callerDigits) return true;
  }
  return false;
}

export function tagCall(
  call: VoiceCall,
  smsIndex: readonly SmsIndexEntry[] = [],
  now: number = Date.now()
): TaggedCall {
  let tag: CallTag;

  switch (call.outcome) {
    case "forwarded":
      tag = "answered";
      break;
    case "answered-ai":
      tag = "ai_answered";
      break;
    case "voicemail":
    case "voicemail-heard":
      tag = "left_vm";
      break;
    case "missed":
      tag = "missed";
      break;
    case "in-progress": {
      // Orphans older than the threshold get tagged `missed` so the
      // reporting view's counts stay honest even if the janitor cron
      // hasn't swept yet (hourly cadence — worst case a 59 min gap).
      const createdMs = Date.parse(call.created_at);
      if (Number.isFinite(createdMs) && now - createdMs > IN_PROGRESS_FRESH_MS) {
        tag = "missed";
      } else {
        tag = "in_progress";
      }
      break;
    }
    default:
      tag = "unknown";
      break;
  }

  const callMs = Date.parse(call.created_at);
  const sentText = Number.isFinite(callMs) ? hasSentTextFor(call, callMs, smsIndex) : false;

  return { call, tag, sentText };
}

export function tagCalls(
  calls: readonly VoiceCall[],
  smsIndex: readonly SmsIndexEntry[] = [],
  now: number = Date.now()
): TaggedCall[] {
  return calls.map((c) => tagCall(c, smsIndex, now));
}
